import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { filter, fromEvent, switchMap } from 'rxjs';
import { FooterBarComponent } from './common/footer-bar/footer-bar.component';
import { NetworkService } from './services/network-service.service';
import { OauthService } from './services/oauth.service';
import { PushNotificationService } from './services/push-notification.service';
import { UpdateService } from './services/update-service.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FooterBarComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly networkService = inject(NetworkService);
  private readonly pushSrv = inject(PushNotificationService);
  // Se inyecta para iniciar la escucha de actualizaciones.
  //Al inyectarlo el constructor del updateService empieza a escuchar.
  private readonly updateService = inject(UpdateService);
  private readonly oauthSrv = inject(OauthService);
  private readonly document = inject(DOCUMENT);

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  /**
   * Arranca oculta a propósito. Las vistas públicas se cargan con loadComponent,
   * así que entre el primer pintado y el constructor del componente hay que
   * esperar la descarga del chunk: si arrancara visible, en cada arranque en
   * frío se veía asomar la barra inferior antes del splash.
   */
  public readonly hideBarras = signal(true);
  public isOnline = this.networkService.isOnline;

  /**
   * La inicialización se realiza aquí porque Firebase requiere que la lógica
   * de configuración se ejecute dentro del contexto del constructor o de un método
   * llamado desde él, para asegurar que las APIs de Firebase se usen correctamente
   * dentro del ciclo de vida de Angular.
   */
  constructor() {
    // Solo inicializamos si ya aceptó los términos previamente (persistencia local)
    const acepto = JSON.parse(localStorage.getItem('acepto') || 'false');
    if (acepto) {
      this.pushSrv.initializeFirebaseMessaging();
    }

    fromEvent(this.document, 'visibilitychange')
      .pipe(
        filter(() => !this.document.hidden),
        switchMap(() => this.oauthSrv.syncSession()),
        takeUntilDestroyed()
      )
      .subscribe();

    /**
     * La visibilidad de la barra la manda la ruta, no cada vista.
     * Las rutas públicas ya declaraban data: { hideNav: true } en app.routes.ts
     * pero nadie lo leía. Mismo criterio que usa BarraComponent.
     */
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.sincronizarBarras());
  }

  private sincronizarBarras(): void {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.hideBarras.set(!!route.snapshot.data['hideNav']);
  }
}
